/**
 * Client-side row mutation helpers for table views.
 *
 * Use these ONLY when both of the following are true:
 *   1. The row count is small and bounded — the full set always fits in memory and we
 *      never need real pagination. Good rule of thumb: a few dozen rows max.
 *   2. The GraphQL query backing the table has no sort, filter, or pagination arguments
 *      — typically because rows are fetched as a nested field of a parent object (e.g.
 *      `shopifyProductByProductListingId { variants { ... } }`) rather than from a
 *      first-class list resolver.
 *
 * If either of those stops being true — the list grows, or the backend gains sort/filter/
 * paginate args — migrate the page to server-side sort/filter instead. Do NOT extend these
 * helpers to try to paper over a larger dataset; client-side mutation of thousands of rows
 * per keystroke is a regression waiting to happen.
 *
 * Server-sorted tables are the preferred pattern (see `CLAUDE.md` → "Sort/filter routing —
 * server-sorted vs client-sorted tables"). These helpers exist to keep the fallback
 * consistent, not to encourage it.
 */

import { SortOrder } from "../../../../../common/query-filters";
import { ExtendableFilterExpression } from "../../../../../common/query-filters/FilterExpression";
import { TableDataWithId } from "../../../../../common/tables/TableDataWithId";

/**
 * Sorts an in-memory array of table rows by a given column and sort order.
 *
 * Use this in any `queryTableData` callback that can't delegate sorting to the server
 * (e.g. when rows are fetched as a nested GraphQL field with no sort arguments).
 *
 * Coerces to number when both values parse cleanly so numeric columns sort numerically;
 * otherwise falls back to a locale-aware string compare with `numeric: true` so mixed
 * alphanumerics like "SKU-2" / "SKU-10" still order intuitively. Null/undefined values
 * sink to the bottom regardless of sort order.
 */
export function sortRows<TRow extends TableDataWithId, TColumnKey extends PropertyKey>(
    rows: TRow[],
    sortColumn: TColumnKey,
    sortOrder: SortOrder,
): TRow[] {
    if (!sortColumn) {
        return rows;
    }

    const direction = sortOrder === SortOrder.DESCENDING ? -1 : 1;
    const columnKey = sortColumn as unknown as string;

    return [...rows].sort((a: TRow, b: TRow) => {
        const aVal = (a as unknown as Record<string, unknown>)[columnKey];
        const bVal = (b as unknown as Record<string, unknown>)[columnKey];

        if (aVal == null && bVal == null) {
            return 0;
        }
        if (aVal == null) {
            return 1;
        }
        if (bVal == null) {
            return -1;
        }

        if (typeof aVal !== "boolean" && typeof bVal !== "boolean") {
            const aNum = typeof aVal === "number" ? aVal : Number(aVal);
            const bNum = typeof bVal === "number" ? bVal : Number(bVal);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
                return (aNum - bNum) * direction;
            }
        }

        return String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: "base" }) * direction;
    });
}

/**
 * Filters an in-memory array of table rows against a list of column-scoped filter expressions
 * from `QueryInput.expressions`. Intended companion to `sortRows` for tables whose data can't
 * delegate filtering to the server.
 *
 * TODO: not yet implemented. When the first client-filtered table needs this, implement the
 * `FilterExpressionType` variants (equals, isTrue, isFalse, isNull, isNotNull, …) here rather
 * than per-page, to match how `sortRows` centralizes client-side sort.
 */
export function filterRows<TRow extends TableDataWithId>(
    rows: TRow[],
    _expressions: ExtendableFilterExpression<TRow>[],
): TRow[] {
    return rows;
}
